// MARK://dhk8/admin-shell/lib/clients/waitlist
// Admin client for dhk8-pos-floor waitlist (Fase 1).
// Lane #163 — vista admin de fila de espera, además del WaitlistPanel POS.
//
// F9.17 / ADR-0008: lecturas Y escrituras por el grafo (pos-floor). El JWT lleva
// el tenant; cada subgraph lo impone vía RLS, así que el tenantId NO va en los
// inputs de las mutations.

import { gqlClient } from '../gql.js'
import {
  AdminWaitlistQuery,
  AdminAddWaitlistEntryMutation,
  AdminNotifyWaitlistEntryMutation,
  AdminLeaveWaitlistEntryMutation,
  AdminSeatWaitlistEntryMutation,
  AdminMoveWaitlistEntryMutation,
  AdminNoShowAttemptWaitlistEntryMutation,
} from '../queries/waitlist.queries.js'

export type WaitlistStatus = 'WAITING' | 'NOTIFIED' | 'SEATED' | 'LEFT' | 'EXPIRED'

/** Dirección de reordenamiento en la cola (PosSeatWaitlistInput no aplica aquí). */
export type MoveDirection = 'UP' | 'DOWN'

export interface WaitlistEntryDto {
  id: string
  tenantId: string
  branchId: string | null
  name: string
  phone: string | null
  partySize: number
  status: WaitlistStatus
  notes: string | null
  estimatedWaitMin: number | null
  position: number
  addedAt: string
  notifiedAt: string | null
  seatedAt: string | null
  seatedSessionId: string | null
  noShowAttempts: number
  queueCode?: string | null
  queueDay?: string | null
}

export interface CreateWaitlistInput {
  name: string
  phone?: string
  partySize: number
  notes?: string
  estimatedWaitMin?: number
  branchId?: string
}

/** Input para sentar una entry (espejo de PosSeatWaitlistInput). serverId es
 *  REQUERIDO en el contrato de pos-floor (abre una sesión de mesa con mesero). */
export interface SeatWaitlistInput {
  tableId: string
  serverId: string
  covers?: number
}

export async function listWaitlist(
  opts: { includeHistory?: boolean; branchId?: string; status?: WaitlistStatus } = {},
  signal?: AbortSignal,
): Promise<WaitlistEntryDto[]> {
  // F9.5 / ADR-0009: lectura por el grafo (pos-floor, tenant del JWT, RLS). Mismo
  // campo waitlist que ya consume el POS. Escrituras siguen REST.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(
    AdminWaitlistQuery,
    { branchId: opts.branchId ?? null, status: opts.status ?? null, includeHistory: opts.includeHistory ?? null },
    ctx,
  ).toPromise()
  if (res.error) throw res.error
  return (res.data?.waitlist ?? []) as unknown as WaitlistEntryDto[]
}

export async function addWaitlistEntry(input: CreateWaitlistInput): Promise<WaitlistEntryDto> {
  // F9.17 / ADR-0008: escritura por el grafo. tenantId lo pone el JWT (no va en input).
  const gqlInput: {
    name: string; partySize: number
    phone?: string; notes?: string; estimatedWaitMin?: number; branchId?: string
  } = { name: input.name, partySize: input.partySize }
  if (input.phone) gqlInput.phone = input.phone
  if (input.notes) gqlInput.notes = input.notes
  if (input.estimatedWaitMin !== undefined) gqlInput.estimatedWaitMin = input.estimatedWaitMin
  if (input.branchId) gqlInput.branchId = input.branchId
  const res = await gqlClient.mutation(AdminAddWaitlistEntryMutation, { input: gqlInput }).toPromise()
  if (res.error) throw res.error
  const e = res.data?.addWaitlistEntry
  if (!e) throw new Error('No se pudo agregar a la fila de espera')
  return e as unknown as WaitlistEntryDto
}

export async function notifyWaitlistEntry(id: string): Promise<WaitlistEntryDto> {
  // F9.17 / ADR-0008: notificación por el grafo (tenant del JWT, IDOR-guard por RLS).
  const res = await gqlClient.mutation(AdminNotifyWaitlistEntryMutation, { id }).toPromise()
  if (res.error) throw res.error
  const e = res.data?.notifyWaitlistEntry
  if (!e) throw new Error('No se pudo notificar al cliente')
  return e as unknown as WaitlistEntryDto
}

export async function leaveWaitlistEntry(id: string): Promise<WaitlistEntryDto> {
  // F9.17 / ADR-0008: salida de la fila por el grafo (tenant del JWT, IDOR-guard por RLS).
  const res = await gqlClient.mutation(AdminLeaveWaitlistEntryMutation, { id }).toPromise()
  if (res.error) throw res.error
  const e = res.data?.leaveWaitlistEntry
  if (!e) throw new Error('No se pudo retirar de la fila de espera')
  return e as unknown as WaitlistEntryDto
}

export async function seatWaitlistEntry(id: string, input: SeatWaitlistInput): Promise<WaitlistEntryDto> {
  // F9.17 / ADR-0008: sentar por el grafo (tenant del JWT, IDOR-guard por RLS). Abre
  // una sesión de mesa → SEATED. tableId + serverId son requeridos; covers opcional.
  const gqlInput: { tableId: string; serverId: string; covers?: number } = {
    tableId: input.tableId,
    serverId: input.serverId,
  }
  if (input.covers !== undefined) gqlInput.covers = input.covers
  const res = await gqlClient.mutation(AdminSeatWaitlistEntryMutation, { id, input: gqlInput }).toPromise()
  if (res.error) throw res.error
  const e = res.data?.seatWaitlistEntry
  if (!e) throw new Error('No se pudo sentar al cliente')
  return e as unknown as WaitlistEntryDto
}

export async function moveWaitlistEntry(id: string, direction: MoveDirection): Promise<WaitlistEntryDto> {
  // F9.17 / ADR-0008: reordenar prioridad por el grafo (tenant del JWT, IDOR-guard por RLS).
  const res = await gqlClient.mutation(AdminMoveWaitlistEntryMutation, { id, direction }).toPromise()
  if (res.error) throw res.error
  const e = res.data?.moveWaitlistEntry
  if (!e) throw new Error('No se pudo mover la entrada en la fila')
  return e as unknown as WaitlistEntryDto
}

export async function attemptNoShowWaitlistEntry(id: string): Promise<WaitlistEntryDto> {
  // F9.17 / ADR-0008: intento sin respuesta por el grafo (tenant del JWT, IDOR-guard
  // por RLS). Incrementa noShowAttempts; al 3er intento la entry pasa a EXPIRED.
  const res = await gqlClient.mutation(AdminNoShowAttemptWaitlistEntryMutation, { id }).toPromise()
  if (res.error) throw res.error
  const e = res.data?.noShowAttemptWaitlistEntry
  if (!e) throw new Error('No se pudo registrar el intento sin respuesta')
  return e as unknown as WaitlistEntryDto
}
